// frontend/src/pages/ProfileGallery.js
import React, { useEffect, useState, useContext, useCallback } from 'react';
import api, { fetchJobStatus } from '../api';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import './Profile.css';
import { useNotification } from '../context/NotificationContext';
import { useI18n } from '../i18n';
import ImageLightbox from '../components/ImageLightbox';
import QuotaAlert from '../components/QuotaAlert';

/**
 * ProfileGallery
 * Handles the display and management (CRUD) of generated profile background images.
 */
function ProfileGallery() {
  const { addNotification, addConfirm } = useNotification();
  const { t } = useI18n();
  const { user, refreshUser } = useContext(AuthContext);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [backgroundJob, setBackgroundJob] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [showPaywall, setShowPaywall] = useState(false);
  const [showQuotaAlert, setShowQuotaAlert] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);
  const navigate = useNavigate();

  const fetchImages = useCallback(async (pageNum = 1, append = false, category = activeTab) => {
    try {
      setLoading(true);
      const res = await api.get(`/profile/images/gallery?category=${category}&page=${pageNum}&limit=20`);
      const newImages = res.data.images || [];
      const totalPages = res.data.pagination?.totalPages || 1;

      setImages(prev => append ? [...prev, ...newImages] : newImages);
      setHasMore(pageNum < totalPages);
      setPage(pageNum);
    } catch (err) {
      console.error('Error fetching gallery:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    setPage(1);
    fetchImages(1, false, activeTab);
  }, [activeTab, fetchImages]);

  useEffect(() => {
    setCurrentGalleryIndex(0);
  }, [images.length, activeTab]);

  const applyGeneratedProfileImage = useCallback(async (result) => {
    if (result && result.image) {
      if (refreshUser) await refreshUser();
      await fetchImages(1, false);
      return true;
    }
    return false;
  }, [refreshUser, fetchImages]);

  useEffect(() => {
    if (!backgroundJob?.id || backgroundJob.status !== 'pending') return undefined;

    const intervalId = window.setInterval(async () => {
      try {
        const res = await fetchJobStatus(backgroundJob.id);
        const job = res.data;
        if (job.status === 'completed') {
          await applyGeneratedProfileImage(job.result);
          setBackgroundJob(null);
          window.clearInterval(intervalId);
        } else if (job.status === 'error') {
          addNotification(t('mutantBox.delayedError'), 'error');
          setBackgroundJob(null);
          window.clearInterval(intervalId);
        }
      } catch (e) {
        console.error('Error polling Profile Image job:', e);
      }
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [backgroundJob, applyGeneratedProfileImage, t, addNotification]);

  useEffect(() => {
    if (user) {
      const isFree = (user.imagePlan || 'free') === 'free';
      const hasImages = (user.imagesUsedThisMonth || 0) >= 1;
      const hasNotSeenModal = !user.hasSeenImagePaywallModal;

      if (isFree && hasImages && hasNotSeenModal) {
        setShowPaywall(true);
        // Marcar como visto en el backend inmediatamente al mostrarlo
        api.put('/users/me/mark-paywall-seen').then(() => {
            if (refreshUser) refreshUser();
        }).catch(err => console.error('Error marking paywall as seen:', err));
      }
    }
  }, [user, refreshUser]);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const res = await api.post('/profile/images/generate');

      if (res.status === 202 && res.data?.jobId) {
        setBackgroundJob({ id: res.data.jobId, status: 'pending' });
      } else {
        await applyGeneratedProfileImage(res.data);
      }
    } catch (err) {
      if (err?.response?.status === 403 || err?.response?.data?.quotaExceeded) {
        setShowQuotaAlert(true);
      }
      addNotification(t('gallery.generateError'), 'error');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      await api.put(`/profile/images/${id}/activate`);
      // Update local state to avoid full reload
      setImages(prev => prev.map(img => ({
        ...img,
        isActive: img.id === id ? true : false
      })));
      // Optional: navigate back to profile?
      // User requirement: "El usuario la puede regenerar o cambiar por otra de la Galería cuando quiera"
      // Maybe stay here.
    } catch (err) {
      console.error('Error activating image:', err);
    }
  };

  const handleDelete = async (id) => {
    addConfirm(t('gallery.deleteConfirm'), async () => {
      try {
        await api.delete(`/profile/images/${id}`);
        setImages(prev => prev.filter(img => img.id !== id));
      } catch (err) {
        console.error('Error deleting image:', err);
      }
    });
  };

  return (
    <div id="gallery-main" className="profile-container" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
      {showPaywall && (
        <div className="modal-overlay" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ maxWidth: '500px', textAlign: 'center', padding: '2rem' }}>
            <h2 style={{ color: 'var(--accent-color, #4db6ac)', marginBottom: '1.5rem' }}>
              {t('gallery.unlockTitle')}
            </h2>
            <p style={{ fontSize: '0.9rem', opacity: 0.9, lineHeight: '1.6', marginBottom: '2rem' }}>
              {t('gallery.unlockBody')}
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                className="category-btn selected"
                style={{ padding: '0.8rem 1.5rem' }}
                onClick={() => navigate('/ajustes')}
              >
                {t('gallery.viewPlans')}
              </button>
              <button
                className="category-btn"
                style={{ padding: '0.8rem 1.5rem', background: 'transparent', border: '1px solid #666' }}
                onClick={() => setShowPaywall(false)}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="profile-header">
        <button
          onClick={() => navigate('/profile')}
          style={{ marginRight: 'auto', background: 'transparent', border: 'none', color: 'var(--text-color)', fontSize: '1.5rem', cursor: 'pointer' }}
        >
           ← {t('common.back')}
        </button>
        <h2 style={{ margin: 0 }}>{t('gallery.title')}</h2>
        {activeTab === 'profile' && (
          <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <button
              onClick={handleGenerate}
              disabled={generating || !!backgroundJob}
              className={(generating || backgroundJob) ? 'btn-processing' : ''}
              style={{
                padding: '0.5rem 1rem',
                background: (generating || backgroundJob) ? '#555' : '#4db6ac',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: (generating || backgroundJob) ? 'default' : 'pointer'
              }}
            >
              {backgroundJob ? t('common.processing') : (generating ? t('common.generating') : t('gallery.generateNew'))}
            </button>
            {backgroundJob && (
              <span style={{ fontSize: '0.7rem', color: '#4db6ac', marginTop: '4px' }}>
                {t('mutantBox.delayedGeneration')}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="gallery-tabs-container" style={{ display: 'flex', gap: '10px', marginBottom: '20px', justifyContent: 'center' }}>
        <button
          className={activeTab === 'profile' ? 'active-tab' : ''}
          onClick={() => setActiveTab('profile')}
          style={{ padding: '0.5rem 1rem', borderRadius: '20px', border: '1px solid #4db6ac', background: activeTab === 'profile' ? '#4db6ac' : 'transparent', color: 'white', cursor: 'pointer' }}
        >
          {t('gallery.profileTab')}
        </button>
        <button
          className={activeTab === 'mutantbox' ? 'active-tab' : ''}
          onClick={() => setActiveTab('mutantbox')}
          style={{ padding: '0.5rem 1rem', borderRadius: '20px', border: '1px solid #4db6ac', background: activeTab === 'mutantbox' ? '#4db6ac' : 'transparent', color: 'white', cursor: 'pointer' }}
        >
          {t('gallery.mutantBoxTab')}
        </button>
        <button
          className={activeTab === 'tasks' ? 'active-tab' : ''}
          onClick={() => setActiveTab('tasks')}
          style={{ padding: '0.5rem 1rem', borderRadius: '20px', border: '1px solid #4db6ac', background: activeTab === 'tasks' ? '#4db6ac' : 'transparent', color: 'white', cursor: 'pointer' }}
        >
          {t('gallery.tasksTab')}
        </button>
      </div>

      {loading && images.length === 0 ? (
        <p>{t('gallery.loading')}</p>
      ) : (
        <div id="gallery-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginTop: '2rem' }}>
          {images.map(img => (
            <div key={img.id} id={currentGalleryIndex === images.indexOf(img) ? "gallery-current-item" : undefined} data-agent-gallery-item={String(img.id)} data-agent-gallery-title={new Date(img.createdAt).toLocaleDateString()} data-agent-gallery-index={String(images.indexOf(img))} style={{ position: 'relative', border: img.isActive ? '2px solid #4db6ac' : '1px solid #444', borderRadius: '8px', overflow: 'hidden' }}>
              <img
                src={img.imageData}
                alt={t('gallery.imageAlt')}
                style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                onClick={() => setLightboxImage(img.imageData)}
              />
              <div style={{ padding: '0.5rem', background: '#222', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', color: '#aaa' }}>{new Date(img.createdAt).toLocaleDateString()}</span>
                {activeTab === 'profile' ? (
                  <>
                    {img.isActive ? (
                      <span style={{ color: '#4db6ac', fontWeight: 'bold', fontSize: '0.8rem' }}>{t('common.activeFem')}</span>
                    ) : (
                      <button data-agent-action="open-gallery-item" onClick={() => { setCurrentGalleryIndex(images.indexOf(img)); handleActivate(img.id); }} style={{ fontSize: '0.8rem', padding: '2px 6px' }}>{t('common.use')}</button>
                    )}
                    <button data-agent-action="delete-gallery-item" onClick={() => handleDelete(img.id)} style={{ background: 'red', border: 'none', color: 'white', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>×</button>
                  </>
                ) : (
                  <span style={{ fontSize: '0.8rem', color: '#aaa' }}>{activeTab === 'mutantbox' ? t('gallery.mutantBoxTab') : t('gallery.task')}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "none" }}>
        <button id="btn-gallery-prev" onClick={() => setCurrentGalleryIndex((v) => Math.max(0, v - 1))}>prev</button>
        <button id="btn-gallery-next" onClick={() => setCurrentGalleryIndex((v) => Math.min(images.length - 1, v + 1))}>next</button>
      </div>

      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage}
          alt={t('gallery.imageAlt')}
          onClose={() => setLightboxImage(null)}
        />
      )}

      <QuotaAlert
        isOpen={showQuotaAlert}
        onClose={() => setShowQuotaAlert(false)}
      />

      {hasMore && !loading && (
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            onClick={() => fetchImages(page + 1, true)}
            style={{ padding: '0.8rem 2rem', background: '#333', color: 'white', border: '1px solid #555', borderRadius: '4px', cursor: 'pointer' }}
          >
            {t('common.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}

export default ProfileGallery;
